import type { CredentialTileSlotLayoutV1, IBaseCredentialMetaData, TEMPLATE_META_DATA } from "../../../../types/credential";
import {
  getAssignmentRefsFromGroupedAssignments,
  sortGroupedAssignmentKeys,
  sortGroupedAssignments,
} from "../../../../util/common";
import type { NullableGroupedAssignments } from "../types/common";
import { createTileSlotLayout, getTileIdsFromSlotLayout } from "./tileSlotLayout";

export const getTileIdsFromGroupedAssignmentsForLayout = (
  groupedAssignments: NullableGroupedAssignments,
): string[] => getAssignmentRefsFromGroupedAssignments({
  groupedAssignments,
  refsSorter: sortGroupedAssignmentKeys,
});

export const getCurrentTileIdsForLayout = ({
  groupedAssignments,
  tileSlotLayout,
}: {
  groupedAssignments: NullableGroupedAssignments;
  tileSlotLayout: CredentialTileSlotLayoutV1 | null;
}): string[] => {
  const existingTileIds = getTileIdsFromSlotLayout({ tileSlotLayout });
  return existingTileIds.length
    ? existingTileIds
    : getTileIdsFromGroupedAssignmentsForLayout(groupedAssignments);
};

export const buildTileSlotLayout = ({
  colsPerRow,
  rowsPerSlide,
  tileIds,
}: {
  colsPerRow: number;
  rowsPerSlide: number;
  tileIds: string[];
}) => createTileSlotLayout({
  colsPerRow,
  rowsPerSlide,
  tileIds,
});

export const buildTileSlotLayoutFromAssignments = ({
  colsPerRow,
  groupedAssignments,
  rowsPerSlide,
}: {
  colsPerRow: number;
  groupedAssignments: NullableGroupedAssignments;
  rowsPerSlide: number;
}) => buildTileSlotLayout({
  colsPerRow,
  rowsPerSlide,
  tileIds: getTileIdsFromGroupedAssignmentsForLayout(groupedAssignments),
});

export const buildTileSlotLayoutPreservingOrder = ({
  colsPerRow,
  groupedAssignments,
  rowsPerSlide,
  tileSlotLayout,
}: {
  colsPerRow: number;
  groupedAssignments: NullableGroupedAssignments;
  rowsPerSlide: number;
  tileSlotLayout: CredentialTileSlotLayoutV1 | null;
}) => buildTileSlotLayout({
  colsPerRow,
  rowsPerSlide,
  tileIds: getCurrentTileIdsForLayout({
    groupedAssignments,
    tileSlotLayout,
  }),
});

export const getSortedGroupedAssignmentsForLayout = ({
  assignmentFieldsOverride,
  groupedAssignments,
  sortingState,
}: {
  assignmentFieldsOverride: IBaseCredentialMetaData["assignment_fields_override"];
  groupedAssignments: NullableGroupedAssignments;
  sortingState: TEMPLATE_META_DATA["sorting_state"];
}) => {
  if (!groupedAssignments) {
    return [];
  }

  return sortGroupedAssignments({
    assignmentFieldsOverride,
    groupedAssignments,
    gridTemplateAreas: undefined,
    sortingState,
  }).sortedGroupedAssignments;
};

export const orderGroupedAssignmentsByTileSlotLayout = ({
  groupedAssignments,
  previousGroupedAssignments,
  tileSlotLayout,
}: {
  groupedAssignments: NullableGroupedAssignments;
  previousGroupedAssignments: NullableGroupedAssignments;
  tileSlotLayout: CredentialTileSlotLayoutV1 | null;
}): NullableGroupedAssignments => {
  if (!groupedAssignments || !tileSlotLayout) {
    return groupedAssignments;
  }

  const tileIds = getTileIdsFromSlotLayout({ tileSlotLayout });
  if (!tileIds.length) {
    return groupedAssignments;
  }

  const tileOrderByTileId = new Map(tileIds.map((tileId, tileOrder) => [tileId, tileOrder]));
  const tileOrderByClientCompanyId = new Map<string, number>();

  for (const previousGroupedAssignment of previousGroupedAssignments ?? []) {
    const previousTileId = getTileIdsFromGroupedAssignmentsForLayout([previousGroupedAssignment])[0];
    const tileOrder = tileOrderByTileId.get(previousTileId);
    if (tileOrder !== undefined) {
      tileOrderByClientCompanyId.set(previousGroupedAssignment.clientCompanyId, tileOrder);
    }
  }

  return groupedAssignments.map((groupedAssignment, inputOrder) => {
    const tileId = getTileIdsFromGroupedAssignmentsForLayout([groupedAssignment])[0];
    return {
      groupedAssignment,
      inputOrder,
      tileOrder: tileOrderByTileId.get(tileId)
        ?? tileOrderByClientCompanyId.get(groupedAssignment.clientCompanyId),
    };
  }).toSorted((assignmentA, assignmentB) => {
    if (assignmentA.tileOrder === undefined) {
      return assignmentB.tileOrder === undefined
        ? assignmentA.inputOrder - assignmentB.inputOrder
        : 1;
    }
    if (assignmentB.tileOrder === undefined) {
      return -1;
    }

    return assignmentA.tileOrder - assignmentB.tileOrder
      || assignmentA.inputOrder - assignmentB.inputOrder;
  }).map(({ groupedAssignment }) => groupedAssignment);
};
